import React from 'react';
import styled from 'styled-components';
import { ReviewsCarouselOption1, ReviewsCarouselOption2, ReviewsCarouselOption3 } from './ReviewsCarousel';
import { getColor, getSpacing } from '../styles/designSystem';

const DemoContainer = styled.div`
  min-height: 100vh;
  background: ${getColor('neutral.gray.50')};
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${getColor('primary.900')}, ${getColor('accent.700')});
  color: white;
  padding: ${getSpacing('3xl')} ${getSpacing('xl')};
  text-align: center;
  
  h1 {
    margin: 0 0 ${getSpacing('md')};
    font-size: 2.5rem;
    font-weight: 300;
  }
  
  p {
    margin: 0;
    font-size: 1.125rem;
    opacity: 0.9;
  }
`;

const DemoSection = styled.div`
  margin-bottom: ${getSpacing('5xl')};
`;

const SectionLabel = styled.div`
  background: ${getColor('accent.600')};
  color: white;
  padding: ${getSpacing('lg')} ${getSpacing('xl')};
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: ${getSpacing('2xl')};
  position: sticky;
  top: 0;
  z-index: 50;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Description = styled.div`
  background: white;
  padding: ${getSpacing('xl')};
  margin: 0 ${getSpacing('xl')} ${getSpacing('xl')};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  h3 {
    margin: 0 0 ${getSpacing('md')};
    color: ${getColor('primary.900')};
    font-size: 1.25rem;
  }
  
  ul {
    margin: 0;
    padding-left: ${getSpacing('xl')};
    color: ${getColor('neutral.gray.700')};
    
    li {
      margin: ${getSpacing('sm')} 0;
      line-height: 1.6;
    }
  }
`;

export const CarouselComparison: React.FC = () => {
  return (
    <DemoContainer>
      <Header>
        <h1>Reviews Carousel - All 3 Options Demo</h1>
        <p>Compare different carousel designs to find your perfect fit</p>
      </Header>

      {/* Option 1 */}
      <DemoSection>
        <SectionLabel>OPTION 1: Clean Grid with Arrow Navigation (Currently on Homepage)</SectionLabel>
        
        <Description>
          <h3>Features:</h3>
          <ul>
            <li>Shows exactly <strong>4 reviews per page</strong> in a clean grid</li>
            <li>Simple left/right arrow buttons for navigation</li>
            <li>Page counter (e.g., "1 / 3") shows progress</li>
            <li>Smooth page transitions</li>
            <li>Card hover effects (lift + shadow)</li>
            <li><strong>Best for:</strong> Professional, clean aesthetic - Recommended for luxury travel</li>
          </ul>
        </Description>
        
        <ReviewsCarouselOption1 />
      </DemoSection>

      {/* Option 2 */}
      <DemoSection>
        <SectionLabel>OPTION 2: Sliding Tray with Smooth Animation</SectionLabel>
        
        <Description>
          <h3>Features:</h3>
          <ul>
            <li><strong>4 reviews visible</strong> at once with smooth slide animation</li>
            <li>Labeled "Previous" and "Next" buttons</li>
            <li>Dot indicators below show which set you're viewing</li>
            <li>Smooth CSS transitions when sliding</li>
            <li>Instagram/Pinterest-style horizontal scrolling</li>
            <li><strong>Best for:</strong> Modern apps, engaging user experience</li>
          </ul>
        </Description>
        
        <ReviewsCarouselOption2 />
      </DemoSection>

      {/* Option 3 */}
      <DemoSection>
        <SectionLabel>OPTION 3: Tab-Based Navigation</SectionLabel>
        
        <Description>
          <h3>Features:</h3>
          <ul>
            <li>Shows exactly <strong>4 reviews per tab</strong></li>
            <li>Tabs labeled with review ranges (e.g., "Reviews 1-4", "Reviews 5-8")</li>
            <li>Click any tab to jump directly to that set</li>
            <li>Active tab clearly highlighted</li>
            <li>Instant switching with no animation delay</li>
            <li><strong>Best for:</strong> Content-heavy sites, quick navigation through many reviews</li>
          </ul>
        </Description>
        
        <ReviewsCarouselOption3 />
      </DemoSection>

      <Header style={{ marginTop: getSpacing('5xl') }}>
        <h1>Which One Do You Like?</h1>
        <p>All 3 options show 4 reviews per row with manual navigation only</p>
      </Header>
    </DemoContainer>
  );
};

export default CarouselComparison;
